import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import config from '../config';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { Settings, MapPin, Globe } from 'lucide-react';
import { useSelectedMetrics, isMetricSelected, getCosClassName, getCpaClassName, useComponentMetrics } from '../utils/metricsUtils';
import MetricsConfigModal from './MetricsConfigModal';
import ModuleVisibilityToggle from './ModuleVisibilityToggle';

import { calculateVariation, renderVariation } from '../utils/variationUtils';
import { colonnesVideoGoogleAds, cumulerVideo, TOTAUX_VIDEO_INITIAUX } from '../utils/metriquesVideo';
import { getDateRangeFromPeriod, calculatePreviousPeriod } from '../utils/periodUtils';
// Délai de debounce pour les appels API (en ms)
const API_DEBOUNCE_DELAY = 300;

// Mapping des noms de pays anglais vers français
const COUNTRY_NAMES_FR = {
  'Afghanistan': 'Afghanistan',
  'Albania': 'Albanie',
  'Algeria': 'Algérie',
  'Andorra': 'Andorre',
  'Angola': 'Angola',
  'Argentina': 'Argentine',
  'Armenia': 'Arménie',
  'Australia': 'Australie',
  'Austria': 'Autriche',
  'Azerbaijan': 'Azerbaïdjan',
  'Bahamas': 'Bahamas',
  'Bahrain': 'Bahreïn',
  'Bangladesh': 'Bangladesh',
  'Belarus': 'Biélorussie',
  'Belgium': 'Belgique',
  'Benin': 'Bénin',
  'Bhutan': 'Bhoutan',
  'Bolivia': 'Bolivie',
  'Bosnia and Herzegovina': 'Bosnie-Herzégovine',
  'Botswana': 'Botswana',
  'Brazil': 'Brésil',
  'Brunei': 'Brunei',
  'Bulgaria': 'Bulgarie',
  'Burkina Faso': 'Burkina Faso',
  'Burundi': 'Burundi',
  'Cambodia': 'Cambodge',
  'Cameroon': 'Cameroun',
  'Canada': 'Canada',
  'Cape Verde': 'Cap-Vert',
  'Central African Republic': 'République centrafricaine',
  'Chad': 'Tchad',
  'Chile': 'Chili',
  'China': 'Chine',
  'Colombia': 'Colombie',
  'Comoros': 'Comores',
  'Congo': 'Congo',
  'Costa Rica': 'Costa Rica',
  'Croatia': 'Croatie',
  'Cuba': 'Cuba',
  'Cyprus': 'Chypre',
  'Czech Republic': 'République tchèque',
  'Czechia': 'Tchéquie',
  'Democratic Republic of the Congo': 'République démocratique du Congo',
  'Denmark': 'Danemark',
  'Djibouti': 'Djibouti',
  'Dominican Republic': 'République dominicaine',
  'Ecuador': 'Équateur',
  'Egypt': 'Égypte',
  'El Salvador': 'Salvador',
  'Equatorial Guinea': 'Guinée équatoriale',
  'Eritrea': 'Érythrée',
  'Estonia': 'Estonie',
  'Eswatini': 'Eswatini',
  'Ethiopia': 'Éthiopie',
  'Fiji': 'Fidji',
  'Finland': 'Finlande',
  'France': 'France',
  'French Guiana': 'Guyane française',
  'French Polynesia': 'Polynésie française',
  'Gabon': 'Gabon',
  'Gambia': 'Gambie',
  'Georgia': 'Géorgie',
  'Germany': 'Allemagne',
  'Ghana': 'Ghana',
  'Greece': 'Grèce',
  'Greenland': 'Groenland',
  'Guadeloupe': 'Guadeloupe',
  'Guatemala': 'Guatemala',
  'Guinea': 'Guinée',
  'Guinea-Bissau': 'Guinée-Bissau',
  'Guyana': 'Guyana',
  'Haiti': 'Haïti',
  'Honduras': 'Honduras',
  'Hong Kong': 'Hong Kong',
  'Hungary': 'Hongrie',
  'Iceland': 'Islande',
  'India': 'Inde',
  'Indonesia': 'Indonésie',
  'Iran': 'Iran',
  'Iraq': 'Irak',
  'Ireland': 'Irlande',
  'Israel': 'Israël',
  'Italy': 'Italie',
  'Ivory Coast': 'Côte d\'Ivoire',
  'Jamaica': 'Jamaïque',
  'Japan': 'Japon',
  'Jordan': 'Jordanie',
  'Kazakhstan': 'Kazakhstan',
  'Kenya': 'Kenya',
  'Kosovo': 'Kosovo',
  'Kuwait': 'Koweït',
  'Kyrgyzstan': 'Kirghizistan',
  'Laos': 'Laos',
  'Latvia': 'Lettonie',
  'Lebanon': 'Liban',
  'Lesotho': 'Lesotho',
  'Liberia': 'Liberia',
  'Libya': 'Libye',
  'Liechtenstein': 'Liechtenstein',
  'Lithuania': 'Lituanie',
  'Luxembourg': 'Luxembourg',
  'Macao': 'Macao',
  'Madagascar': 'Madagascar',
  'Malawi': 'Malawi',
  'Malaysia': 'Malaisie',
  'Maldives': 'Maldives',
  'Mali': 'Mali',
  'Malta': 'Malte',
  'Martinique': 'Martinique',
  'Mauritania': 'Mauritanie',
  'Mauritius': 'Maurice',
  'Mayotte': 'Mayotte',
  'Mexico': 'Mexique',
  'Moldova': 'Moldavie',
  'Monaco': 'Monaco',
  'Mongolia': 'Mongolie',
  'Montenegro': 'Monténégro',
  'Morocco': 'Maroc',
  'Mozambique': 'Mozambique',
  'Myanmar': 'Myanmar',
  'Namibia': 'Namibie',
  'Nepal': 'Népal',
  'Netherlands': 'Pays-Bas',
  'New Caledonia': 'Nouvelle-Calédonie',
  'New Zealand': 'Nouvelle-Zélande',
  'Nicaragua': 'Nicaragua',
  'Niger': 'Niger',
  'Nigeria': 'Nigeria',
  'North Korea': 'Corée du Nord',
  'North Macedonia': 'Macédoine du Nord',
  'Norway': 'Norvège',
  'Oman': 'Oman',
  'Pakistan': 'Pakistan',
  'Palestine': 'Palestine',
  'Panama': 'Panama',
  'Papua New Guinea': 'Papouasie-Nouvelle-Guinée',
  'Paraguay': 'Paraguay',
  'Peru': 'Pérou',
  'Philippines': 'Philippines',
  'Poland': 'Pologne',
  'Portugal': 'Portugal',
  'Puerto Rico': 'Porto Rico',
  'Qatar': 'Qatar',
  'Reunion': 'La Réunion',
  'Réunion': 'La Réunion',
  'Romania': 'Roumanie',
  'Russia': 'Russie',
  'Rwanda': 'Rwanda',
  'Saint Lucia': 'Sainte-Lucie',
  'Saint Martin': 'Saint-Martin',
  'San Marino': 'Saint-Marin',
  'Saudi Arabia': 'Arabie saoudite',
  'Senegal': 'Sénégal',
  'Serbia': 'Serbie',
  'Seychelles': 'Seychelles',
  'Sierra Leone': 'Sierra Leone',
  'Singapore': 'Singapour',
  'Slovakia': 'Slovaquie',
  'Slovenia': 'Slovénie',
  'Somalia': 'Somalie',
  'South Africa': 'Afrique du Sud',
  'South Korea': 'Corée du Sud',
  'South Sudan': 'Soudan du Sud',
  'Spain': 'Espagne',
  'Sri Lanka': 'Sri Lanka',
  'Sudan': 'Soudan',
  'Suriname': 'Suriname',
  'Sweden': 'Suède',
  'Switzerland': 'Suisse',
  'Syria': 'Syrie',
  'Taiwan': 'Taïwan',
  'Tajikistan': 'Tadjikistan',
  'Tanzania': 'Tanzanie',
  'Thailand': 'Thaïlande',
  'Togo': 'Togo',
  'Trinidad and Tobago': 'Trinité-et-Tobago',
  'Tunisia': 'Tunisie',
  'Turkey': 'Turquie',
  'Turkmenistan': 'Turkménistan',
  'Uganda': 'Ouganda',
  'Ukraine': 'Ukraine',
  'United Arab Emirates': 'Émirats arabes unis',
  'United Kingdom': 'Royaume-Uni',
  'United States': 'États-Unis',
  'Uruguay': 'Uruguay',
  'Uzbekistan': 'Ouzbékistan',
  'Vatican City': 'Vatican',
  'Venezuela': 'Venezuela',
  'Vietnam': 'Viêt Nam',
  'Yemen': 'Yémen',
  'Zambia': 'Zambie',
  'Zimbabwe': 'Zimbabwe'
};

// Fonction pour traduire un nom de pays en français
const translateCountryName = (englishName) => {
  if (!englishName) return 'Pays inconnu';
  return COUNTRY_NAMES_FR[englishName] || englishName;
};

/**
 * Composant modulaire pour afficher les données géographiques
 * Ce composant charge ses propres données depuis l'endpoint modulaire
 * Supporte multiple providers : 'google' (Google Ads) et 'microsoft' (Microsoft Ads)
 */

// Mapping des endpoints par provider
const API_ENDPOINTS = {
  google: {
    geography: '/api/dashboard/components/geography',
    regions: '/api/dashboard/components/geography/regions'
  },
  microsoft: {
    geography: '/api/dashboard/microsoft-ads/geography',
    regions: '/api/dashboard/microsoft-ads/geography/regions'
  }
};

const GeographyTableModular = React.memo(({
  selectedAccount,
  selectedPeriod,
  customStartDate,
  customEndDate,
  formatNumber,
  isVisible = true,
  onToggleVisibility,
  selectedCampaignFilter,
  selectedCampaignTypeFilter,
  // Filtre réseau (Microsoft Ads) : la route résout les campagnes concernées.
  selectedNetworkFilter = null,
  token,
  setError,
  // Nouvelles props pour optimisation filtrage
  rawData = null,
  enableClientSideFiltering = false,
  preloadedData = null,
  // Props pour filtrage par labels
  selectedLabelsFilter = [],
  labelFilteredCampaignIds = null,
  // Props pour comparaison de périodes
  previousPeriodData = null,
  compareEnabled = false,
  comparisonType = '',
  loadingComparison = false,
  userRole = 'USER',
  // Props pour la visibilité client
  isClientModuleVisible,
  toggleClientModuleVisibility,
  moduleId = 'geography',
  // Prop pour le provider (Google Ads par défaut)
  provider = 'google'
}) => {
  // Obtenir les endpoints pour le provider
  const endpoints = API_ENDPOINTS[provider] || API_ENDPOINTS.google;
  const selectedMetrics = useSelectedMetrics();
  const [showMetricsConfig, setShowMetricsConfig] = useState(false);

  // Configuration des métriques pour ce composant (utilise moduleId pour supporter multi-provider)
  const componentMetrics = useComponentMetrics(moduleId, selectedAccount?.id || 'global');

  // Helper pour vérifier si une métrique est sélectionnée pour ce composant
  const isComponentMetricSelected = useCallback((metricKey) => {
    return componentMetrics.metrics.includes(metricKey);
  }, [componentMetrics.metrics]);

  // Refs pour le debouncing et l'annulation des requêtes
  const abortControllerRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const lastRequestKeyRef = useRef(null);

  // États locaux pour les données
  const [geoData, setGeoData] = useState([]);
  const [previousGeoData, setPreviousGeoData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPreviousPeriod, setLoadingPreviousPeriod] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);





  // Configuration du rendu des métriques (utilisé pour l'ordre dynamique)
  const metricConfig = useMemo(() => ({
    impressions: {
      label: 'Impressions',
      sortKey: 'impressions',
      renderCell: (geo, prevGeo, hasComparison) => (
        <>
          {formatNumber(geo.impressions || 0)}
          {hasComparison && renderVariation(calculateVariation(geo.impressions || 0, prevGeo.impressions || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{formatNumber(totals.impressions)}</strong>
          {previousTotals && renderVariation(calculateVariation(totals.impressions, previousTotals.impressions))}
        </>
      )
    },
    ctr: {
      label: 'CTR',
      sortKey: 'ctr',
      renderCell: (geo, prevGeo, hasComparison) => (
        <>
          {formatNumber(geo.ctr || 0, 2)}%
          {hasComparison && renderVariation(calculateVariation(geo.ctr || 0, prevGeo.ctr || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{formatNumber(totals.ctr, 2)}%</strong>
          {previousTotals && renderVariation(calculateVariation(totals.ctr, previousTotals.ctr))}
        </>
      )
    },
    clics: {
      label: 'Clics',
      sortKey: 'clicks',
      renderCell: (geo, prevGeo, hasComparison) => (
        <>
          {formatNumber(geo.clicks || 0)}
          {hasComparison && renderVariation(calculateVariation(geo.clicks || 0, prevGeo.clicks || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{formatNumber(totals.clicks)}</strong>
          {previousTotals && renderVariation(calculateVariation(totals.clicks, previousTotals.clicks))}
        </>
      )
    },
    cpc: {
      label: 'CPC',
      sortKey: 'cpc',
      renderCell: (geo, prevGeo, hasComparison) => (
        <>
          {"\u20AC"}{formatNumber(geo.cpc || 0, 2)}
          {hasComparison && renderVariation(calculateVariation(geo.cpc || 0, prevGeo.cpc || 0), true)}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{"\u20AC"}{formatNumber(totals.cpc, 2)}</strong>
          {previousTotals && renderVariation(calculateVariation(totals.cpc, previousTotals.cpc), true)}
        </>
      )
    },
    cout: {
      label: 'Coût',
      sortKey: 'cost',
      renderCell: (geo, prevGeo, hasComparison) => (
        <>
          {"\u20AC"}{formatNumber(geo.cost || 0, 2)}
          {hasComparison && renderVariation(calculateVariation(geo.cost || 0, prevGeo.cost || 0), true)}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{"\u20AC"}{formatNumber(totals.cost, 2)}</strong>
          {previousTotals && renderVariation(calculateVariation(totals.cost, previousTotals.cost), true)}
        </>
      )
    },
    taux_conv: {
      label: 'Taux conv.',
      sortKey: 'conversionRate',
      renderCell: (geo, prevGeo, hasComparison) => (
        <>
          {formatNumber(geo.conversionRate || 0, 2)}%
          {hasComparison && renderVariation(calculateVariation(geo.conversionRate || 0, prevGeo.conversionRate || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{formatNumber(totals.conversionRate, 2)}%</strong>
          {previousTotals && renderVariation(calculateVariation(totals.conversionRate, previousTotals.conversionRate))}
        </>
      )
    },
    conv: {
      label: 'Conv.',
      sortKey: 'conversions',
      renderCell: (geo, prevGeo, hasComparison) => (
        <>
          {formatNumber(geo.conversions || 0, 1)}
          {hasComparison && renderVariation(calculateVariation(geo.conversions || 0, prevGeo.conversions || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{formatNumber(totals.conversions, 1)}</strong>
          {previousTotals && renderVariation(calculateVariation(totals.conversions, previousTotals.conversions))}
        </>
      )
    },
    cpa: {
      label: 'CPA',
      sortKey: 'cpa',
      getClassName: (geo) => getCpaClassName(geo.cpa || 0, selectedAccount?.id || 'global', moduleId),
      getTotalClassName: (totals) => getCpaClassName(totals.cpa, selectedAccount?.id || 'global', moduleId),
      renderCell: (geo, prevGeo, hasComparison) => (
        <>
          {(geo.cpa || 0) >= 0 ? `\u20AC${formatNumber(geo.cpa || 0, 2)}` : '-'}
          {hasComparison && renderVariation(calculateVariation(geo.cpa || 0, prevGeo.cpa || 0), true)}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{totals.cpa >= 0 ? `\u20AC${formatNumber(totals.cpa, 2)}` : '-'}</strong>
          {previousTotals && renderVariation(calculateVariation(totals.cpa, previousTotals.cpa), true)}
        </>
      )
    },
    valeur_conv: {
      label: 'Valeur Conv.',
      sortKey: 'conversionsValue',
      renderCell: (geo, prevGeo, hasComparison) => (
        <>
          {"\u20AC"}{formatNumber(geo.conversionsValue || 0, 2)}
          {hasComparison && renderVariation(calculateVariation(geo.conversionsValue || 0, prevGeo.conversionsValue || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{"\u20AC"}{formatNumber(totals.conversionsValue, 2)}</strong>
          {previousTotals && renderVariation(calculateVariation(totals.conversionsValue, previousTotals.conversionsValue))}
        </>
      )
    },
    cos: {
      label: 'COS',
      sortKey: 'cos',
      getClassName: (geo) => getCosClassName(geo.cos || 0, selectedAccount?.id || 'global', moduleId),
      getTotalClassName: (totals) => getCosClassName(totals.cos, selectedAccount?.id || 'global', moduleId),
      renderCell: (geo, prevGeo, hasComparison) => {
        const cos = geo.cos || 0;
        const prevCos = prevGeo && (prevGeo.conversionsValue || 0) > 0 ? (prevGeo.cost / (prevGeo.conversionsValue || 1) * 100) : 0;
        return (
          <>
            {formatNumber(cos, 1)}%
            {hasComparison && renderVariation(calculateVariation(cos, prevCos), true)}
          </>
        );
      },
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{formatNumber(totals.cos, 1)}%</strong>
          {previousTotals && renderVariation(calculateVariation(totals.cos, previousTotals.cos), true)}
        </>
      )
    },
    roas: {
      label: 'ROAS',
      sortKey: 'roas',
      renderCell: (geo, prevGeo, hasComparison) => {
        const roas = (geo.cost || 0) > 0 ? ((geo.conversionsValue || 0) / geo.cost) * 100 : 0;
        const prevRoas = prevGeo && (prevGeo.cost || 0) > 0 ? ((prevGeo.conversionsValue || 0) / prevGeo.cost) * 100 : 0;
        return (
          <>
            {roas >= 0 ? `${formatNumber(roas, 1)}%` : '-'}
            {hasComparison && renderVariation(calculateVariation(roas, prevRoas))}
          </>
        );
      },
      renderTotal: (totals, previousTotals) => {
        const roas = (totals.cost || 0) > 0 ? ((totals.conversionsValue || 0) / totals.cost) * 100 : 0;
        const prevRoas = previousTotals && (previousTotals.cost || 0) > 0 ? ((previousTotals.conversionsValue || 0) / previousTotals.cost) * 100 : 0;
        return (
          <>
            <strong>{roas >= 0 ? `${formatNumber(roas, 1)}%` : '-'}</strong>
            {previousTotals && renderVariation(calculateVariation(roas, prevRoas))}
          </>
        );
      }
    },
    roi: {
      label: 'ROI',
      sortKey: 'roi',
      renderCell: (geo, prevGeo, hasComparison) => {
        const roi = (geo.cost || 0) > 0 ? ((geo.conversionsValue || 0) - geo.cost) / geo.cost : 0;
        const prevRoi = prevGeo && (prevGeo.cost || 0) > 0 ? ((prevGeo.conversionsValue || 0) - prevGeo.cost) / prevGeo.cost : 0;
        return (
          <>
            {formatNumber(roi, 2)}
            {hasComparison && renderVariation(calculateVariation(roi, prevRoi))}
          </>
        );
      },
      renderTotal: (totals, previousTotals) => {
        const roi = (totals.cost || 0) > 0 ? ((totals.conversionsValue || 0) - totals.cost) / totals.cost : 0;
        const prevRoi = previousTotals && (previousTotals.cost || 0) > 0 ? ((previousTotals.conversionsValue || 0) - previousTotals.cost) / previousTotals.cost : 0;
        return (
          <>
            <strong>{formatNumber(roi, 2)}</strong>
            {previousTotals && renderVariation(calculateVariation(roi, prevRoi))}
          </>
        );
      }
    },
    panier_moyen: {
      label: 'Panier moyen',
      sortKey: 'panierMoyen',
      renderCell: (geo, prevGeo, hasComparison) => {
        const panierMoyen = (geo.conversions || 0) > 0 ? (geo.conversionsValue || 0) / geo.conversions : 0;
        const prevPanierMoyen = prevGeo && (prevGeo.conversions || 0) > 0 ? (prevGeo.conversionsValue || 0) / prevGeo.conversions : 0;
        return (
          <>
            {"\u20AC"}{formatNumber(panierMoyen, 2)}
            {hasComparison && renderVariation(calculateVariation(panierMoyen, prevPanierMoyen))}
          </>
        );
      },
      renderTotal: (totals, previousTotals) => {
        const panierMoyen = (totals.conversions || 0) > 0 ? (totals.conversionsValue || 0) / totals.conversions : 0;
        const prevPanierMoyen = previousTotals && (previousTotals.conversions || 0) > 0 ? (previousTotals.conversionsValue || 0) / previousTotals.conversions : 0;
        return (
          <>
            <strong>{"\u20AC"}{formatNumber(panierMoyen, 2)}</strong>
            {previousTotals && renderVariation(calculateVariation(panierMoyen, prevPanierMoyen))}
          </>
        );
      }
    },
    ...colonnesVideoGoogleAds({ formatNumber, renderVariation, calculateVariation })
  }), [formatNumber, selectedAccount?.id, renderVariation, calculateVariation]);

  // États pour le drill-down régions (expansion sous la ligne)
  const [expandedCountry, setExpandedCountry] = useState(null); // countryCode du pays expansé
  const [regionsData, setRegionsData] = useState([]);
  const [previousRegionsData, setPreviousRegionsData] = useState([]);
  const [regionsLoading, setRegionsLoading] = useState(false);

  // États pour le tri
  const [sortConfig, setSortConfig] = useState({ key: 'cost', direction: 'desc' });

  // Tri des régions (utilise le même sortConfig que les pays)
  const sortedRegionsData = useMemo(() => {
    if (!regionsData.length) return [];

    const sorted = [...regionsData];
    sorted.sort((a, b) => {
      let aValue, bValue;

      // Mapper la clé de tri pour les régions
      const key = sortConfig.key === 'country' ? 'region' : sortConfig.key;

      if (key === 'region') {
        // Pour les noms de région, tri alphabétique
        aValue = String(a.region || '').toLowerCase();
        bValue = String(b.region || '').toLowerCase();
        return sortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      // Pour les valeurs numériques
      aValue = Number(a[key]) || 0;
      bValue = Number(b[key]) || 0;

      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });
    return sorted;
  }, [regionsData, sortConfig]);

  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Couleurs pour l'histogramme (même style que l'histogramme "📈 Résultats")
  const CLICK_COLOR = '#82ca9d'; // Vert comme dans l'histogramme Résultats
  const CONVERSION_COLOR = '#8884d8'; // Mauve comme dans l'histogramme Résultats

  // Charger les données quand le composant est monté ou que les dépendances changent
  useEffect(() => {
    console.log(`🌍 [GEOGRAPHY-${provider.toUpperCase()}-EFFECT] useEffect triggered - compareEnabled: ${compareEnabled}, comparisonType: ${comparisonType}, isDataLoaded: ${isDataLoaded}`);

    // Ne pas charger les données si le module est masqué
    if (!isVisible) return;

    // Fonction pour charger les données de période précédente
    const loadPreviousPeriodData = async (signal) => {
      if (!compareEnabled || !comparisonType || !selectedAccount?.id || !token) {
        setPreviousGeoData([]);
        setLoadingPreviousPeriod(false);
        return;
      }

      let currentStartDate = customStartDate;
      let currentEndDate = customEndDate;
      if (!currentStartDate || !currentEndDate) {
        const dateRange = getDateRangeFromPeriod(selectedPeriod);
        currentStartDate = dateRange.startDate;
        currentEndDate = dateRange.endDate;
      }

      const prevPeriod = calculatePreviousPeriod(currentStartDate, currentEndDate, comparisonType);

      setLoadingPreviousPeriod(true);
      try {
        const prevRequestBody = {
          sources: [selectedAccount.id],
          period: 'CUSTOM',
          customStartDate: prevPeriod.startDate,
          customEndDate: prevPeriod.endDate,
          campaignFilter: selectedCampaignFilter || null,
          campaignTypeFilter: selectedCampaignTypeFilter || null,
          networkFilter: selectedNetworkFilter || null
        };

        if (labelFilteredCampaignIds && labelFilteredCampaignIds.length > 0) {
          prevRequestBody.campaignIds = labelFilteredCampaignIds;
        }

        const prevResponse = await axios.post(
          `${config.API_URL}${endpoints.geography}`,
          prevRequestBody,
          {
            headers: { Authorization: `Bearer ${token}` },
            signal
          }
        );

        if (prevResponse.data.success) {
          const prevCountries = prevResponse.data.data.countries || [];
          setPreviousGeoData(prevCountries);
        }
      } catch (prevError) {
        if (prevError.name !== 'CanceledError') {
          console.warn(`⚠️ [GEOGRAPHY] Erreur chargement période précédente:`, prevError.message);
        }
        setPreviousGeoData([]);
      } finally {
        setLoadingPreviousPeriod(false);
      }
    };

    // Mode preload : utiliser les données déjà chargées en parallèle (seulement si aucun filtre)
    if (preloadedData && preloadedData.success && !selectedCampaignFilter && !selectedCampaignTypeFilter && selectedLabelsFilter.length === 0) {
      console.log(`🌍 [GEOGRAPHY-${provider.toUpperCase()}-EFFECT] Using PRELOADED data path`);
      const countries = preloadedData.data.countries || [];
      setGeoData(countries);
      setIsDataLoaded(true);
      setLoading(false);
      // Charger les données de période précédente si comparaison activée
      if (compareEnabled && comparisonType) {
        console.log(`🌍 [GEOGRAPHY-${provider.toUpperCase()}-EFFECT] Preload path: loading comparison data`);
        loadPreviousPeriodData();
      } else {
        setPreviousGeoData([]);
      }
      return;
    }

    // Mode optimisé : utiliser rawData avec filtrage côté client
    if (enableClientSideFiltering && rawData && selectedLabelsFilter.length === 0) {
      console.log(`🌍 [GEOGRAPHY-${provider.toUpperCase()}-EFFECT] Using RAW DATA path`);
      // Extraire les données géographiques depuis rawData
      const accountData = Object.values(rawData).find(data => data && data.geography);
      const allGeoData = accountData?.geography || [];

      setGeoData(allGeoData);
      setIsDataLoaded(true);
      setLoading(false);
      // Charger les données de période précédente si comparaison activée
      if (compareEnabled && comparisonType) {
        console.log(`🌍 [GEOGRAPHY-${provider.toUpperCase()}-EFFECT] Raw data path: loading comparison data`);
        loadPreviousPeriodData();
      } else {
        setPreviousGeoData([]);
      }
      return;
    }

    // Mode classique : appel API modulaire
    console.log(`🌍 [GEOGRAPHY-${provider.toUpperCase()}-EFFECT] Using REGULAR API path - selectedAccount: ${!!selectedAccount}, selectedPeriod: ${selectedPeriod}, token: ${!!token}`);
    if (!selectedAccount || !selectedPeriod || !token) {
      console.log(`🌍 [GEOGRAPHY-${provider.toUpperCase()}-EFFECT] Early return - missing required data`);
      return;
    }

    // Créer une clé unique pour cette requête (pour la déduplication)
    const requestKey = JSON.stringify({
      accountId: selectedAccount.id,
      period: selectedPeriod,
      customStartDate,
      customEndDate,
      campaignFilter: selectedCampaignFilter,
      campaignTypeFilter: selectedCampaignTypeFilter,
      networkFilter: selectedNetworkFilter || null,
      labelFilteredCampaignIds: labelFilteredCampaignIds?.join(','),
      compareEnabled,
      comparisonType,
      provider
    });

    // Si c'est la même requête que la précédente, ignorer
    if (lastRequestKeyRef.current === requestKey) {
      console.log(`🌍 [GEOGRAPHY-${provider.toUpperCase()}-EFFECT] Skip - requestKey unchanged`);
      return;
    }
    console.log(`🌍 [GEOGRAPHY-${provider.toUpperCase()}-EFFECT] New request - key changed, proceeding with API call`);

    // Mettre loading=true immédiatement (avant le debounce) pour éviter que le composant retourne null
    setLoading(true);

    // Annuler le timer de debounce précédent
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Annuler la requête précédente si elle existe
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Debounce : attendre avant de lancer la requête
    debounceTimerRef.current = setTimeout(() => {
      console.log(`🌍 [GEOGRAPHY-${provider.toUpperCase()}-DEBOUNCE] Debounce callback executed - compareEnabled: ${compareEnabled}, comparisonType: ${comparisonType}`);
      // Créer un nouveau AbortController
      const controller = new AbortController();
      abortControllerRef.current = controller;
      lastRequestKeyRef.current = requestKey;

      const loadGeographyData = async () => {
        try {
          console.log(`🌍 [GEOGRAPHY-${provider.toUpperCase()}-LOAD] loadGeographyData started`);
          const requestBody = {
            sources: [selectedAccount.id],
            period: selectedPeriod,
            campaignFilter: selectedCampaignFilter || null,
            campaignTypeFilter: selectedCampaignTypeFilter || null,
            networkFilter: selectedNetworkFilter || null
          };

          // Ajouter les campaignIds filtrés par labels si présents
          if (labelFilteredCampaignIds && labelFilteredCampaignIds.length > 0) {
            requestBody.campaignIds = labelFilteredCampaignIds;
          }

          // Ajouter les dates personnalisées si période CUSTOM
          if (selectedPeriod === 'CUSTOM') {
            if (!customStartDate || !customEndDate) return;
            requestBody.customStartDate = customStartDate;
            requestBody.customEndDate = customEndDate;
          }

          const response = await axios.post(
            `${config.API_URL}${endpoints.geography}`,
            requestBody,
            {
              headers: { Authorization: `Bearer ${token}` },
              signal: controller.signal
            }
          );

          console.log(`🌍 [GEOGRAPHY-${provider.toUpperCase()}-LOAD] API response received - success: ${response.data.success}`);
          if (response.data.success) {
            const countries = response.data.data.countries || [];
            console.log(`🌍 [GEOGRAPHY-${provider.toUpperCase()}-LOAD] Got ${countries.length} countries - compareEnabled: ${compareEnabled}, comparisonType: ${comparisonType}`);
            setGeoData(countries);
            setIsDataLoaded(true);

            // Charger les données de la période précédente si comparaison activée
            if (compareEnabled && comparisonType) {
              console.log(`🌍 [GEOGRAPHY-${provider.toUpperCase()}] Chargement données comparaison: ${comparisonType}`);
              let currentStartDate = customStartDate;
              let currentEndDate = customEndDate;
              if (!currentStartDate || !currentEndDate) {
                const dateRange = getDateRangeFromPeriod(selectedPeriod);
                currentStartDate = dateRange.startDate;
                currentEndDate = dateRange.endDate;
              }
              const prevPeriod = calculatePreviousPeriod(currentStartDate, currentEndDate, comparisonType);
              console.log(`🌍 [GEOGRAPHY-${provider.toUpperCase()}] Période précédente: ${prevPeriod.startDate} → ${prevPeriod.endDate}`);

              try {
                const prevRequestBody = {
                  sources: [selectedAccount.id],
                  period: 'CUSTOM',
                  customStartDate: prevPeriod.startDate,
                  customEndDate: prevPeriod.endDate,
                  campaignFilter: selectedCampaignFilter || null,
                  campaignTypeFilter: selectedCampaignTypeFilter || null,
                  networkFilter: selectedNetworkFilter || null
                };

                if (labelFilteredCampaignIds && labelFilteredCampaignIds.length > 0) {
                  prevRequestBody.campaignIds = labelFilteredCampaignIds;
                }

                const prevResponse = await axios.post(
                  `${config.API_URL}${endpoints.geography}`,
                  prevRequestBody,
                  {
                    headers: { Authorization: `Bearer ${token}` },
                    signal: controller.signal
                  }
                );

                if (prevResponse.data.success) {
                  const prevCountries = prevResponse.data.data.countries || [];
                  console.log(`✅ [GEOGRAPHY-${provider.toUpperCase()}] Données comparaison chargées: ${prevCountries.length} pays`);
                  setPreviousGeoData(prevCountries);
                } else {
                  console.warn(`⚠️ [GEOGRAPHY-${provider.toUpperCase()}] API returned success=false pour période précédente`);
                  setPreviousGeoData([]);
                }
              } catch (prevError) {
                if (prevError.name !== 'CanceledError') {
                  console.warn(`⚠️ [GEOGRAPHY-${provider.toUpperCase()}] Erreur chargement période précédente:`, prevError.message);
                }
                setPreviousGeoData([]);
              }
            } else {
              setPreviousGeoData([]);
            }
          } else {
            setError('Erreur lors du chargement des données géographiques');
          }
        } catch (error) {
          // Ignorer les erreurs d'annulation
          if (error.name === 'CanceledError') {
            console.log(`🌍 [GEOGRAPHY-${provider.toUpperCase()}-LOAD] Request CANCELED`);
          } else {
            console.error(`🌍 [GEOGRAPHY-${provider.toUpperCase()}] Erreur API:`, error.message);
            setError('Erreur lors du chargement des données géographiques');
          }
        } finally {
          setLoading(false);
        }
      };

      loadGeographyData();
    }, API_DEBOUNCE_DELAY);

    // Cleanup : annuler seulement le timer de debounce
    // Note: on ne fait PAS abort() ici car le cleanup s'exécute à chaque re-render,
    // même quand requestKey est unchanged et qu'on fait un early return.
    // L'abort() est géré dans le corps de l'effet, seulement quand on démarre une NOUVELLE requête.
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [isVisible, selectedAccount, selectedPeriod, customStartDate, customEndDate, selectedCampaignFilter, selectedCampaignTypeFilter, selectedNetworkFilter, token, setError, preloadedData, selectedLabelsFilter, labelFilteredCampaignIds, compareEnabled, comparisonType, endpoints, provider, rawData, enableClientSideFiltering]);

  // Les données sont déjà filtrées côté serveur (via campaignIds)
  // Pas de filtrage client-side car les données géographiques sont agrégées sans campaignId
  const filteredGeoData = useMemo(() => {
    return geoData;
  }, [geoData]);

  // Fonction de tri memoized
  const sortedGeoData = useMemo(() => {
    if (!filteredGeoData.length) return [];

    const sorted = [...filteredGeoData];
    sorted.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      
      if (sortConfig.key === 'country') {
        // Pour les noms de pays, tri alphabétique
        aValue = String(aValue || '').toLowerCase();
        bValue = String(bValue || '').toLowerCase();
        return sortConfig.direction === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      // Pour les valeurs numériques
      aValue = Number(aValue) || 0;
      bValue = Number(bValue) || 0;
      
      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });
    return sorted;
  }, [filteredGeoData, sortConfig]);

  // Pagination des données triées
  const totalPages = Math.ceil(sortedGeoData.length / itemsPerPage);
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedGeoData.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedGeoData, currentPage, itemsPerPage]);

  // Réinitialiser la pagination quand les données changent
  useEffect(() => {
    setCurrentPage(1);
  }, [filteredGeoData]);

  // Calcul des totaux memoized (sur les données filtrées)
  const totals = useMemo(() => {
    if (!filteredGeoData.length) return {};

    const baseTotals = filteredGeoData.reduce((acc, country) => ({
      impressions: acc.impressions + (country.impressions || 0),
      clicks: acc.clicks + (country.clicks || 0),
      cost: acc.cost + (country.cost || 0),
      conversions: acc.conversions + (country.conversions || 0),
      conversionsValue: acc.conversionsValue + (country.conversionsValue || 0),
      ...cumulerVideo(acc, country)
    }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0, ...TOTAUX_VIDEO_INITIAUX });

    // Calculer les métriques dérivées
    return {
      ...baseTotals,
      ctr: baseTotals.impressions > 0 ? (baseTotals.clicks / baseTotals.impressions * 100) : 0,
      cpc: baseTotals.clicks > 0 ? (baseTotals.cost / baseTotals.clicks) : 0,
      conversionRate: baseTotals.clicks > 0 ? (baseTotals.conversions / baseTotals.clicks * 100) : 0,
      cpa: baseTotals.conversions > 0 ? (baseTotals.cost / baseTotals.conversions) : 0,
      cos: baseTotals.conversionsValue > 0 ? (baseTotals.cost / baseTotals.conversionsValue * 100) : 0
    };
  }, [filteredGeoData]);

  // Calcul des totaux de la période précédente
  const previousTotals = useMemo(() => {
    if (!compareEnabled || !previousGeoData.length) return null;

    const baseTotals = previousGeoData.reduce((acc, country) => ({
      impressions: acc.impressions + (country.impressions || 0),
      clicks: acc.clicks + (country.clicks || 0),
      cost: acc.cost + (country.cost || 0),
      conversions: acc.conversions + (country.conversions || 0),
      conversionsValue: acc.conversionsValue + (country.conversionsValue || 0),
      ...cumulerVideo(acc, country)
    }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0, ...TOTAUX_VIDEO_INITIAUX });

    return {
      ...baseTotals,
      ctr: baseTotals.impressions > 0 ? (baseTotals.clicks / baseTotals.impressions * 100) : 0,
      cpc: baseTotals.clicks > 0 ? (baseTotals.cost / baseTotals.clicks) : 0,
      conversionRate: baseTotals.clicks > 0 ? (baseTotals.conversions / baseTotals.clicks * 100) : 0,
      cpa: baseTotals.conversions > 0 ? (baseTotals.cost / baseTotals.conversions) : 0,
      cos: baseTotals.conversionsValue > 0 ? (baseTotals.cost / baseTotals.conversionsValue * 100) : 0
    };
  }, [compareEnabled, previousGeoData]);

  // Données pour l'histogramme mixte (top 10 pays par conversions)
  const chartData = useMemo(() => {
    if (!filteredGeoData.length) return [];
    
    return filteredGeoData
      .filter(country => country.conversions > 0 || country.clicks > 0)
      .sort((a, b) => b.conversions - a.conversions)
      .slice(0, 10)
      .map(country => ({
        pays: (() => {
          const translated = translateCountryName(country.country);
          return translated.length > 15 ? translated.substring(0, 15) + '...' : translated;
        })(),
        clics: country.clicks || 0,
        conversions: Math.round(country.conversions) || 0
      }));
  }, [filteredGeoData]);

  // Handler de tri memoized
  const handleSort = useCallback((key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'desc' ? 'asc' : 'desc'
    }));
  }, []);

  // Fonction pour toggle l'expansion d'un pays (charger/masquer les régions)
  const toggleCountryExpansion = useCallback(async (country) => {
    if (!selectedAccount || !token || !country.countryCode) {
      return;
    }

    // Si déjà expansé, on ferme
    if (expandedCountry === country.countryCode) {
      setExpandedCountry(null);
      setRegionsData([]);
      setPreviousRegionsData([]);
      return;
    }

    // Sinon on charge les régions
    setExpandedCountry(country.countryCode);
    setRegionsLoading(true);
    setRegionsData([]);
    setPreviousRegionsData([]);

    try {
      const requestBody = {
        sources: [selectedAccount.id],
        countryCode: country.countryCode,
        period: selectedPeriod,
        campaignFilter: selectedCampaignFilter || null
      };

      if (selectedPeriod === 'CUSTOM') {
        requestBody.customStartDate = customStartDate;
        requestBody.customEndDate = customEndDate;
      }

      if (labelFilteredCampaignIds && labelFilteredCampaignIds.length > 0) {
        requestBody.campaignIds = labelFilteredCampaignIds;
      }

      const response = await axios.post(
        `${config.API_URL}${endpoints.regions}`,
        requestBody,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setRegionsData(response.data.data.regions || []);

        // Charger les données de la période précédente si comparaison activée
        if (compareEnabled && comparisonType) {
          let currentStartDate = customStartDate;
          let currentEndDate = customEndDate;
          if (!currentStartDate || !currentEndDate) {
            const dateRange = getDateRangeFromPeriod(selectedPeriod);
            currentStartDate = dateRange.startDate;
            currentEndDate = dateRange.endDate;
          }
          const prevPeriod = calculatePreviousPeriod(currentStartDate, currentEndDate, comparisonType);

          try {
            const prevRequestBody = {
              sources: [selectedAccount.id],
              countryCode: country.countryCode,
              period: 'CUSTOM',
              customStartDate: prevPeriod.startDate,
              customEndDate: prevPeriod.endDate,
              campaignFilter: selectedCampaignFilter || null
            };

            if (labelFilteredCampaignIds && labelFilteredCampaignIds.length > 0) {
              prevRequestBody.campaignIds = labelFilteredCampaignIds;
            }

            const prevResponse = await axios.post(
              `${config.API_URL}${endpoints.regions}`,
              prevRequestBody,
              {
                headers: { Authorization: `Bearer ${token}` }
              }
            );

            if (prevResponse.data.success) {
              const prevRegions = prevResponse.data.data.regions || [];
              setPreviousRegionsData(prevRegions);
            }
          } catch (prevError) {
            console.warn(`⚠️ [REGIONS] Erreur chargement période précédente:`, prevError.message);
            setPreviousRegionsData([]);
          }
        }
      } else {
        console.error('🗺️ [REGIONS] Erreur:', response.data.error);
      }
    } catch (error) {
      console.error('🗺️ [REGIONS] Erreur:', error);
    } finally {
      setRegionsLoading(false);
    }
  }, [selectedAccount, token, selectedPeriod, customStartDate, customEndDate, selectedCampaignFilter, labelFilteredCampaignIds, expandedCountry, compareEnabled, comparisonType]);

  // Pour les clients : masquer complètement si le module est fermé
  if (userRole === 'CLIENT' && !isVisible) {
    return null;
  }

  // Ne pas afficher le composant si pas de données (même pendant le loading)
  if (isDataLoaded && filteredGeoData.length === 0) {
    return null;
  }

  // Si pas encore chargé et pas en cours de loading, ne pas afficher non plus
  if (!loading && !isDataLoaded) {
    return null;
  }

  // Pour les clients, ne pas afficher si le module est masqué
  const clientModuleVisible = isClientModuleVisible ? isClientModuleVisible(moduleId) : true;
  if (userRole === 'CLIENT' && !clientModuleVisible) {
    return null;
  }

  return (
    <div className="campaigns-table-section">
      <div className="section-header">
        <h4><MapPin size={16} />&nbsp;Origine géographique</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Bouton configuration des métriques - visible uniquement pour non-clients */}
          {userRole !== 'CLIENT' && (
            <button
              onClick={() => setShowMetricsConfig(true)}
              className="metrics-settings-btn"
              title="Configurer les métriques"
            >
              <Settings size={16} />
            </button>
          )}
          {isClientModuleVisible && toggleClientModuleVisibility ? (
            <ModuleVisibilityToggle
              isVisible={clientModuleVisible}
              onToggle={toggleClientModuleVisibility}
              userRole={userRole}
              moduleId={moduleId}
            />
          ) : (
            <button
              onClick={onToggleVisibility}
              className="section-toggle-btn"
              title={isVisible ? "Masquer la section" : "Afficher la section"}
            >
              {isVisible ? '−' : '+'}
            </button>
          )}
        </div>
      </div>

      {(userRole !== 'CLIENT' || clientModuleVisible) && isVisible && (
        <>
          {loading ? (
            <div className="loading-message" style={{ textAlign: 'center' }}>
              <div className="loading-spinner" style={{ margin: '0 auto' }}></div>
              Chargement des données géographiques...<br /><br />
            </div>
          ) : compareEnabled && (loadingComparison || loadingPreviousPeriod) ? (
            <div className="loading-message" style={{ textAlign: 'center', padding: '20px' }}>
              <div className="loading-spinner" style={{ margin: '0 auto 10px' }}></div>
              Chargement des données de comparaison...
            </div>
          ) : !isDataLoaded ? (
            <div className="loading-message" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              <div>
                <h4 style={{ margin: '0 0 10px 0', color: '#888', display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}><Globe size={16} /> Origine géographique</h4>
                <p style={{ margin: '0', fontSize: '0.9em' }}>
                  Sélectionnez un compte et une période pour voir les données par pays.
                </p>
              </div>
            </div>
          ) : filteredGeoData.length === 0 ? (
            <div className="loading-message" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              <div>
                <h4 style={{ margin: '0 0 10px 0', color: '#888', display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}><Globe size={16} /> Aucune donnée géographique</h4>
                <p style={{ margin: '0', fontSize: '0.9em' }}>
                  Aucune donnée géographique trouvée pour la période sélectionnée.
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Histogramme mixte clics/conversions (style identique à "📈 Résultats") */}
              {chartData.length > 0 && (
                <div
                  style={{
                    width: '100%',
                    height: '400px',
                    background: 'white',
                    borderRadius: '12px',
                    padding: '20px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                    marginBottom: '20px'
                  }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={chartData}
                      margin={{
                        top: 20,
                        right: 30,
                        left: 20,
                        bottom: 20
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis 
                        dataKey="pays" 
                        stroke="#6b7280"
                        tick={{ fontSize: 12 }}
                        angle={-45}
                        textAnchor="end"
                        height={40}
                        interval={0}
                      />
                      <YAxis 
                        yAxisId="left"
                        orientation="left"
                        stroke={CLICK_COLOR}
                        tick={{ fontSize: 12 }}
                        label={{ value: 'Clics', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }}
                      />
                      <YAxis 
                        yAxisId="right"
                        orientation="right"
                        stroke={CONVERSION_COLOR}
                        tick={{ fontSize: 12 }}
                        label={{ value: 'Conversions', angle: 90, position: 'insideRight', style: { textAnchor: 'middle' } }}
                      />
                      <Tooltip 
                        formatter={(value, name) => [
                          formatNumber(value), 
                          name === 'Clics' ? 'Clics' : 'Conversions'
                        ]}
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                        }}
                        cursor={{ fill: '#f9fafb' }}
                      />
                      <Legend />
                      <Bar 
                        dataKey="clics" 
                        yAxisId="left"
                        fill={CLICK_COLOR} 
                        fillOpacity={0.8}
                        name="Clics"
                        radius={[4, 4, 0, 0]}
                      />
                      <Bar 
                        dataKey="conversions" 
                        yAxisId="right"
                        fill={CONVERSION_COLOR} 
                        fillOpacity={0.8}
                        name="Conversions"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Tableau détaillé */}
              <div className="campaigns-table-wrapper">
                <table className="campaigns-table" key={selectedMetrics.join(",")} style={{ tableLayout: "auto" }}>
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('country')} className="sortable">
                        Pays {sortConfig.key === 'country' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                      {/* Rendu dynamique des colonnes selon l'ordre configuré */}
                      {componentMetrics.order.filter(key => isComponentMetricSelected(key) && metricConfig[key]).map(metricKey => {
                        const config = metricConfig[metricKey];
                        return (
                          <th key={metricKey} onClick={() => handleSort(config.sortKey)} className="sortable numeric">
                            {config.label} {sortConfig.key === config.sortKey && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedData.length === 0 ? (
                      <tr>
                        <td colSpan="11" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                          <div>
                            <h4 style={{ margin: '0 0 10px 0', color: '#888', display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}><Globe size={16} /> Origine géographique non disponible</h4>
                            <p style={{ margin: '0', fontSize: '0.9em' }}>
                              Aucune donnée géographique trouvée pour la période sélectionnée.
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      paginatedData.flatMap((country, index) => {
                        const cos = country.cos || 0;
                        const hasCountryCode = !!country.countryCode;
                        const isExpanded = expandedCountry === country.countryCode;

                        // Trouver la ligne correspondante dans les données de la période précédente
                        // Utiliser countryCode si disponible, sinon fallback sur le nom du pays
                        const prevCountry = compareEnabled && previousGeoData.length > 0
                          ? previousGeoData.find(p => {
                              // Si les deux ont un countryCode non vide, matcher dessus
                              if (p.countryCode && country.countryCode) {
                                return p.countryCode === country.countryCode;
                              }
                              // Sinon matcher sur le nom du pays
                              return p.country === country.country;
                            })
                          : null;
                        const hasComparison = !!prevCountry;
                        const prevCos = prevCountry && (prevCountry.conversionsValue || 0) > 0 ? (prevCountry.cost / (prevCountry.conversionsValue || 1) * 100) : 0;

                        const rows = [
                          <tr
                            key={`country-${index}`}
                            onClick={() => hasCountryCode && toggleCountryExpansion(country)}
                            style={{ cursor: hasCountryCode ? 'pointer' : 'default' }}
                            className={hasCountryCode ? 'clickable-row' : ''}
                            title={hasCountryCode ? `Cliquez pour voir les régions de ${translateCountryName(country.country)}` : ''}
                          >
                            <td>
                              {hasCountryCode && (
                                <span style={{ marginRight: '8px', color: '#9ca3af', fontSize: '0.8em' }}>
                                  {isExpanded ? '▼' : '▶'}
                                </span>
                              )}
                              {translateCountryName(country.country)}
                            </td>
                            {/* Rendu dynamique des cellules selon l'ordre configuré */}
                            {componentMetrics.order.filter(key => isComponentMetricSelected(key) && metricConfig[key]).map(metricKey => {
                              const config = metricConfig[metricKey];
                              const className = config.getClassName ? `numeric ${config.getClassName(country)}` : 'numeric';
                              return (
                                <td key={metricKey} className={className}>
                                  {config.renderCell(country, prevCountry || {}, hasComparison)}
                                </td>
                              );
                            })}
                          </tr>
                        ];

                        // Ajouter les lignes de régions si le pays est expansé
                        if (isExpanded) {
                          if (regionsLoading) {
                            rows.push(
                              <tr key={`regions-loading-${index}`} style={{ background: '#f8fafc' }}>
                                <td colSpan="11" style={{ padding: '12px 12px 12px 32px', color: '#6b7280', fontStyle: 'italic' }}>
                                  Chargement des régions...
                                </td>
                              </tr>
                            );
                          } else if (sortedRegionsData.length === 0) {
                            rows.push(
                              <tr key={`regions-empty-${index}`} style={{ background: '#f8fafc' }}>
                                <td colSpan="11" style={{ padding: '12px 12px 12px 32px', color: '#9ca3af', fontStyle: 'italic' }}>
                                  Aucune région disponible
                                </td>
                              </tr>
                            );
                          } else {
                            sortedRegionsData.forEach((region, regionIndex) => {
                              // Trouver la région correspondante dans les données de la période précédente
                              const prevRegion = compareEnabled && previousRegionsData.length > 0
                                ? previousRegionsData.find(p => p.region === region.region)
                                : null;
                              const hasRegionComparison = !!prevRegion;

                              rows.push(
                                <tr key={`region-${index}-${regionIndex}`} style={{ background: '#f8fafc' }}>
                                  <td style={{ paddingLeft: '32px', color: '#6b7280' }}>
                                    ↳ {region.region || 'Région inconnue'}
                                  </td>
                                  {/* Rendu dynamique des cellules de région selon l'ordre configuré */}
                                  {componentMetrics.order.filter(key => isComponentMetricSelected(key) && metricConfig[key]).map(metricKey => {
                                    const config = metricConfig[metricKey];
                                    const hasClassName = config.getClassName;
                                    const className = hasClassName ? `numeric ${config.getClassName(region)}` : 'numeric';
                                    const style = hasClassName ? { opacity: 0.85 } : { color: '#6b7280' };
                                    return (
                                      <td key={metricKey} className={className} style={style}>
                                        {config.renderCell(region, prevRegion || {}, hasRegionComparison)}
                                      </td>
                                    );
                                  })}
                                </tr>
                              );
                            });
                          }
                        }

                        return rows;
                      })
                    )}
                  </tbody>

                  {sortedGeoData.length > 0 && (
                    <tfoot>
                      <tr className="total-row">
                        <td className="device-name"><strong>TOTAL</strong></td>
                        {/* Rendu dynamique des totaux selon l'ordre configuré */}
                        {componentMetrics.order.filter(key => isComponentMetricSelected(key) && metricConfig[key]).map(metricKey => {
                          const config = metricConfig[metricKey];
                          const className = config.getTotalClassName ? `numeric ${config.getTotalClassName(totals)}` : 'numeric';
                          return (
                            <td key={metricKey} className={className}>
                              {config.renderTotal(totals, previousTotals)}
                            </td>
                          );
                        })}
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>

              {/* Contrôles de pagination */}
              {totalPages > 1 && (
                <div className="pagination-controls">
                  <div className="pagination-info">
                    <span>
                      Affichage {(currentPage - 1) * itemsPerPage + 1}-{Math.min(currentPage * itemsPerPage, sortedGeoData.length)} sur {sortedGeoData.length} pays
                    </span>
                  </div>
                  <div className="pagination-buttons">
                    <button 
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="pagination-btn"
                    >
                      ← Précédent
                    </button>
                    <span className="page-numbers">
                      Page {currentPage} sur {totalPages}
                    </span>
                    <button 
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="pagination-btn"
                    >
                      Suivant →
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Modal de configuration des métriques */}
      <MetricsConfigModal
        isOpen={showMetricsConfig}
        onClose={() => setShowMetricsConfig(false)}
        componentId={moduleId}
        accountId={selectedAccount?.id || 'global'}
        componentTitle="Géographie"
      />
    </div>
  );
});

GeographyTableModular.displayName = 'GeographyTableModular';

export default GeographyTableModular;